import React from "react";
import { parseMoney } from "util/manualDeliveryForm";
import { dayLabel, pickupRange, windowText, zoneAbbreviation } from "util/manualDeliveryTime";
import { formatCents } from "util/manualShipment";

// The pickup line from the quote details: the window we will request, its zone, and why it starts early.
function pickupLine(offer, timezone) {
  const details = offer.details || {};
  const range = pickupRange(details);
  if (range) {
    const zone = zoneAbbreviation(details.pickup_start_at, details.time_zone || timezone);
    const when = `${offer.name} picks up ${range}${zone ? ` (${zone})` : ""}.`;
    if (details.pickup_window_kind === "fixed_city_window") {
      return `${when} ${offer.name} only accepts its fixed city pickup times and may refuse this one.`;
    }
    const buffer = details.pickup_buffer_minutes;
    return buffer > 0 ? `${when} That is ${buffer} minutes before your window starts.` : when;
  }
  if (details.ServiceName) return `${offer.name} service: ${details.ServiceName}.`;
  return "";
}

export default function ReviewStep({ offer, oldPriceCents, form, todayIso, timezone }) {
  const own = offer.kind === "own";
  const price = Number.isInteger(offer.priceCents) ? formatCents(offer.priceCents) : "No price";
  const showOld = Number.isInteger(oldPriceCents) && oldPriceCents !== offer.priceCents;
  const name = `${form.first} ${form.last}`.trim() || "No name";
  const address = [form.line1, form.line2, [form.city, form.province, form.postal].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  const contact = [form.phone, form.email].filter(Boolean).join(" · ") || "No contact details";
  const pickup = own ? "" : pickupLine(offer, timezone);
  const signature = form.signature || (form.alcohol && offer.signatureForced);
  const packages = own
    ? "Not needed for your own driver"
    : [
        `${form.count} ${form.count === 1 ? "package" : "packages"}`,
        form.weight.trim() ? `${form.weight.trim()} kg` : "",
        form.alcohol ? "contains alcohol" : "",
        signature ? "signature required" : "",
      ]
        .filter(Boolean)
        .join(" · ");
  let note = `Includes your markup. We book at exactly this price; if ${offer.name} changes it, you confirm again.`;
  if (own) {
    note = Number.isInteger(offer.priceCents)
      ? "Your store delivery fee for this address."
      : "The store fee could not be worked out for this address; your driver delivers it either way.";
  }

  return (
    <div className="md-review">
      <div className="md-price-card">
        <div className="md-price-card__label">Price</div>
        <div className="md-price-card__row">
          <span className="md-price-card__price">{price}</span>
          {showOld ? (
            <span className="md-price__old">
              <span className="md-visually-hidden">Was </span>
              {formatCents(oldPriceCents)}
            </span>
          ) : null}
          <span className="md-muted">{offer.name}</span>
        </div>
        <div className="md-sub md-muted">{note}</div>
      </div>
      <dl className="md-summary">
        <dt>Deliver to</dt>
        <dd>
          <span className="md-medium">{name}</span>
          <span>{address}</span>
          <span className="md-sub md-muted">{contact}</span>
        </dd>
        <dt className="md-summary__divided">When</dt>
        <dd className="md-summary__divided">
          <span className="md-medium">{`${dayLabel(form.date, todayIso)}, ${windowText(form.start)}`}</span>
          {pickup ? <span className="md-sub md-muted">{pickup}</span> : null}
        </dd>
        <dt className="md-summary__divided">Order</dt>
        <dd className="md-summary__divided">
          <span className="md-mono">{formatCents(Math.round(parseMoney(form.value) * 100))}</span>
          {form.items.trim() ? <span className="md-sub md-muted">{form.items.trim()}</span> : null}
        </dd>
        <dt className="md-summary__divided">Packages</dt>
        <dd className="md-summary__divided">{packages}</dd>
      </dl>
      {own ? null : (
        <p className="md-sub md-muted">
          Changing any detail gets a new price. Date, time, value and notes can't be edited after booking.
        </p>
      )}
    </div>
  );
}
